import api from '../api/api';

/**
 * Service for tracking recruiter performance metrics
 */
class PerformanceTrackingService {
  /**
   * Track a login event
   * @returns {Promise<Object>} The API response
   */
  static async trackLogin() {
    try {
      const response = await api.post('performance/track', {
        activityType: 'login'
      });
      return response.data;
    } catch (error) {
      console.error('Error tracking login:', error);
      return null;
    }
  }

  /**
   * Track a logout event
   * @returns {Promise<Object>} The API response
   */
  static async trackLogout() {
    try {
      const response = await api.post('performance/track', {
        activityType: 'logout'
      });
      return response.data;
    } catch (error) {
      console.error('Error tracking logout:', error);
      return null;
    }
  }

  /**
   * Track a job posting event
   * @param {Object} jobDetails The job details
   * @returns {Promise<Object>} The API response
   */
  static async trackJobPosting(jobDetails) {
    try {
      const response = await api.post('performance/track', {
        activityType: 'post_job',
        details: {
          jobId: jobDetails.id,
          jobTitle: jobDetails.title
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error tracking job posting:', error);
      return null;
    }
  }

  /**
   * Track a candidate review event
   * @param {Object} candidateDetails The candidate details
   * @returns {Promise<Object>} The API response
   */
  static async trackCandidateReview(candidateDetails) {
    try {
      const response = await api.post('performance/track', {
        activityType: 'review_candidate',
        details: {
          candidateId: candidateDetails.id,
          candidateName: candidateDetails.name
        }
      });
      return response.data;
    } catch (error) {
      // Silent fail - analytics shouldn't break user experience
      return null;
    }
  }

  /**
   * Track AI usage
   * @param {Object} usageDetails The AI usage details
   * @returns {Promise<Object>} The API response
   */
  static async trackAIUsage(usageDetails) {
    try {
      const response = await api.post('performance/track', {
        activityType: 'use_ai',
        details: {
          feature: usageDetails.feature,
          creditsUsed: usageDetails.creditsUsed || 1
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error tracking AI usage:', error);
      return null;
    }
  }

  /**
   * Track active time
   * @param {number} minutes The number of active minutes
   * @returns {Promise<Object>} The API response
   */
  static async trackActiveTime(minutes) {
    try {
      const response = await api.post('performance/track', {
        activityType: 'active_time',
        details: {
          minutes: minutes
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error tracking active time:', error);
      return null;
    }
  }

  /**
   * Track database search
   * @param {Object} searchDetails The search details
   * @returns {Promise<Object>} The API response
   */
  static async trackDatabaseSearch(searchDetails) {
    try {
      const response = await api.post('performance/track', {
        activityType: 'database_search',
        details: {
          query: searchDetails.query || '',
          filters: searchDetails.filters || {}
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error tracking database search:', error);
      return null;
    }
  }

  /**
   * Track an interview scheduling event
   * @param {Object} interviewDetails The interview details
   * @returns {Promise<Object>} The API response
   */
  static async trackInterviewScheduled(interviewDetails) {
    try {
      const response = await api.post('performance/track', {
        activityType: 'schedule_interview',
        details: {
          candidateId: interviewDetails.candidateId,
          candidateName: interviewDetails.candidateName,
          jobId: interviewDetails.jobId,
          interviewDate: interviewDetails.date,
          interviewType: interviewDetails.type
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error tracking interview scheduling:', error);
      return null;
    }
  }

  /**
   * Get performance data
   * @param {string} timeRange The time range (week, month, quarter, year)
   * @returns {Promise<Object>} The performance data
   */
  static async getPerformanceData(timeRange = 'week') {
    try {
      const response = await api.get(`/api/performance/data?timeRange=${timeRange}`);

      return response.data;
    } catch (error) {
      console.error('Error getting performance data:', error);
      return null;
    }
  }
}

export default PerformanceTrackingService;
